// Data visualization for posts.
//
// Meghan adds a chart with an HTML card:
//
//   <figure class="viz" data-viz='{"type":"line","x":"year","y":"failures","series":"cause","src":"/content/files/failures.csv"}'>
//     <figcaption>Dam failures in the United States by cause, 1900 to 2020</figcaption>
//   </figure>
//
// Data can come from: "src" (CSV or JSON URL, e.g. a Ghost file upload), "data" (inline array of
// objects), "from": "table" (the nearest table above the figure), or a <script type="application/json">
// inside the figure holding the whole spec.
//
// Types: line, area, bar, stacked-bar, dot, histogram, slope. Options: x, y, series, xLabel, yLabel,
// horizontal, percent, curve ("linear"|"step"|"monotone"), height, colors, sort, domain, tableView (default true).
//
// Rendering uses Observable Plot, loaded from a CDN only when a page contains a chart.
// Every chart gets a hover tooltip, a legend when there is more than one series,
// and a collapsible data table so the numbers are available without the picture.

const D3_URL = "https://cdn.jsdelivr.net/npm/d3@7.9.0/dist/d3.min.js";
const PLOT_URL = "https://cdn.jsdelivr.net/npm/@observablehq/plot@0.6.17/dist/plot.umd.min.js";

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => reject(new Error("Could not load the chart library"));
        document.head.appendChild(s);
    });
}

let plotPromise = null;
function loadPlot() {
    if (window.Plot && window.Plot.plot) return Promise.resolve(window.Plot);
    if (plotPromise) return plotPromise;
    // Plot's browser bundle needs d3 on the page first
    plotPromise = (window.d3 ? Promise.resolve() : loadScript(D3_URL))
        .then(() => loadScript(PLOT_URL))
        .then(() => window.Plot);
    return plotPromise;
}

// ---- data helpers ---------------------------------------------------------

function parseCSV(text) {
    const rows = [];
    let row = [], field = "", quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
            } else field += c;
        } else if (c === '"') quoted = true;
        else if (c === ",") { row.push(field); field = ""; }
        else if (c === "\n" || c === "\r") {
            if (c === "\r" && text[i + 1] === "\n") i++;
            row.push(field); rows.push(row); row = []; field = "";
        } else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    const header = rows.shift().map((h) => h.trim());
    return rows.filter((r) => r.some((v) => v.trim() !== "")).map((r) => {
        const o = {};
        header.forEach((h, i) => (o[h] = coerce(r[i] ?? "")));
        return o;
    });
}

const ISO_DATE = /^\d{4}-\d{2}(-\d{2})?(T[\d:.]+Z?)?$/;
function coerce(v) {
    if (v == null) return v;
    if (typeof v !== "string") return v;
    const s = v.trim();
    if (s === "") return null;
    if (ISO_DATE.test(s)) return new Date(s.length === 7 ? s + "-01" : s);
    const n = Number(s.replace(/[,$%]/g, ""));
    if (!Number.isNaN(n) && /^[-+]?[\d,]*\.?\d+%?$/.test(s.replace(/^\$/, ""))) return n;
    return s;
}

function tableToData(table) {
    const head = Array.from(table.tHead?.rows[0]?.cells || table.rows[0]?.cells || []).map((c) => c.textContent.trim());
    const body = Array.from(table.tBodies[0]?.rows || Array.from(table.rows).slice(1));
    return body.map((r) => {
        const o = {};
        head.forEach((h, i) => (o[h] = coerce(r.cells[i]?.textContent ?? "")));
        return o;
    });
}

async function resolveData(el, spec) {
    if (Array.isArray(spec.data)) return spec.data.map((d) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, coerce(v)])));
    if (spec.from === "table" || spec.table) {
        let table = spec.table ? document.querySelector(spec.table) : null;
        if (!table) {
            // nearest table above the figure
            let node = el;
            while (node && !table) {
                let prev = node.previousElementSibling;
                while (prev && !table) {
                    table = prev.matches("table") ? prev : prev.querySelector("table");
                    prev = prev.previousElementSibling;
                }
                node = node.parentElement;
                if (node && node.classList.contains("gh-content")) break;
            }
        }
        if (!table) throw new Error("No table found above this chart");
        return tableToData(table);
    }
    if (spec.src) {
        const res = await fetch(spec.src);
        if (!res.ok) throw new Error(`Could not load ${spec.src}`);
        const text = await res.text();
        return spec.src.trim().toLowerCase().endsWith(".json") || text.trim().startsWith("[")
            ? JSON.parse(text)
            : parseCSV(text);
    }
    throw new Error("No data: give the chart a src, data, or from: \"table\"");
}

// ---- theme ----------------------------------------------------------------

function themeVars(el) {
    const cs = getComputedStyle(el);
    const v = (name, fallback) => (cs.getPropertyValue(name) || fallback).trim();
    return {
        series: [1, 2, 3, 4, 5, 6].map((i) => v(`--series-${i}`, "#2b5c9e")),
        seq: [1, 2, 3, 4, 5, 6].map((i) => v(`--seq-${i}`, "#2b5c9e")),
        div: [v("--div-neg"), v("--div-mid"), v("--div-pos")],
        ink: v("--ink", "#111"),
        inkSoft: v("--ink-soft", "#444"),
        grid: v("--viz-grid", "rgba(0,0,0,.08)"),
        axis: v("--viz-axis", "rgba(0,0,0,.35)"),
        paper: v("--paper", "#fffff8"),
    };
}

// ---- rendering ------------------------------------------------------------

const isYearField = (name, values) =>
    /year/i.test(name || "") ||
    (values.length && values.every((d) => Number.isInteger(d) && d > 1000 && d < 3000));

function buildMarks(Plot, spec, data, t) {
    const { x, y, series } = spec;
    const type = (spec.type || "line").toLowerCase();
    const curve = spec.curve || (type === "area" ? "linear" : "linear");
    const horizontal = !!spec.horizontal;
    const tip = spec.tip === false ? undefined : true;
    const strokeW = 1.75;
    const marks = [];

    const color = series ? { stroke: series, fill: series } : {};
    const seriesStroke = series ? series : t.series[0];
    const seriesFill = series ? series : t.series[0];

    switch (type) {
        case "line":
            marks.push(Plot.ruleY([0], { stroke: t.axis }));
            marks.push(Plot.lineY(data, { x, y, stroke: seriesStroke, strokeWidth: strokeW, curve }));
            marks.push(Plot.dot(data, Plot.pointerX({ x, y, stroke: seriesStroke, fill: t.paper, r: 4, strokeWidth: 1.5 })));
            if (tip) marks.push(Plot.tip(data, Plot.pointerX({ x, y, ...(series ? { stroke: series } : {}) })));
            marks.push(Plot.crosshairX(data, { x, y, color: t.inkSoft }));
            break;
        case "area":
            marks.push(Plot.areaY(data, { x, y, fill: seriesFill, fillOpacity: series ? 0.35 : 0.25, curve, ...(series && spec.stack === false ? {} : {}) }));
            marks.push(Plot.lineY(data, { x, y, stroke: seriesStroke, strokeWidth: 1.25, curve }));
            marks.push(Plot.ruleY([0], { stroke: t.axis }));
            if (tip) marks.push(Plot.tip(data, Plot.pointerX({ x, y, ...(series ? { fill: series } : {}) })));
            break;
        case "bar":
        case "stacked-bar": {
            const stacked = type === "stacked-bar" || spec.stack === true;
            const grouped = series && !stacked;
            const opts = { fill: seriesFill, inset: 1, rx: 1.5, ...(tip ? { tip: true } : {}) };
            if (horizontal) {
                marks.push(Plot.barX(data, { y: grouped ? series : x, x: y, ...(grouped ? { fy: x } : {}), ...opts, sort: spec.sort ? { y: "-x" } : undefined }));
                marks.push(Plot.ruleX([0], { stroke: t.axis }));
            } else {
                marks.push(Plot.barY(data, { x: grouped ? series : x, y, ...(grouped ? { fx: x } : {}), ...opts, sort: spec.sort ? { x: "-y" } : undefined }));
                marks.push(Plot.ruleY([0], { stroke: t.axis }));
            }
            break;
        }
        case "dot":
        case "scatter":
            marks.push(Plot.dot(data, { x, y, stroke: seriesStroke, fill: seriesFill, fillOpacity: 0.55, r: spec.r || 4, ...(tip ? { tip: true } : {}) }));
            if (spec.trend) marks.push(Plot.linearRegressionY(data, { x, y, stroke: t.inkSoft, ci: 0 }));
            break;
        case "histogram":
            marks.push(Plot.rectY(data, Plot.binX({ y: "count" }, { x, fill: seriesFill, thresholds: spec.bins || 20, inset: 1, rx: 1.5, ...(tip ? { tip: true } : {}) })));
            marks.push(Plot.ruleY([0], { stroke: t.axis }));
            break;
        case "slope":
            // two-period comparison: x has two values, one line per series
            marks.push(Plot.line(data, { x, y, z: series, stroke: seriesStroke, strokeWidth: 1.5, marker: "circle" }));
            marks.push(Plot.text(data, Plot.selectFirst({ x, y, z: series, text: series, textAnchor: "end", dx: -8, fill: t.ink })));
            marks.push(Plot.text(data, Plot.selectLast({ x, y, z: series, text: series, textAnchor: "start", dx: 8, fill: t.ink })));
            break;
        default:
            throw new Error(`Unknown chart type "${type}"`);
    }
    return marks;
}

function render(Plot, el, spec, data) {
    const t = themeVars(el);
    const width = Math.max(280, el.clientWidth || 600);
    const type = (spec.type || "line").toLowerCase();
    const xs = data.map((d) => d[spec.x]).filter((v) => v != null);
    const categoricalX = xs.some((v) => typeof v === "string");
    const seriesValues = spec.series ? Array.from(new Set(data.map((d) => d[spec.series]))) : [];
    const height = spec.height || Math.round(Math.min(420, Math.max(220, width * 0.5)));
    const fontSize = 13;

    const xScale = {
        label: spec.xLabel ?? (spec.x || null),
        grid: false,
        ...(isYearField(spec.x, xs) ? { tickFormat: "d" } : {}),
        ...(spec.domain?.x ? { domain: spec.domain.x } : {}),
        ...(categoricalX && (type === "bar" || type === "stacked-bar") ? { tickSize: 0 } : {}),
    };
    const yScale = {
        label: spec.yLabel ?? (spec.y || null),
        grid: true,
        nice: true,
        ...(spec.percent ? { percent: true } : {}),
        ...(spec.domain?.y ? { domain: spec.domain.y } : {}),
        ...(type === "bar" && spec.horizontal ? { tickSize: 0 } : {}),
    };

    const plot = Plot.plot({
        width,
        height,
        marginLeft: spec.marginLeft || (spec.horizontal ? 110 : 48),
        marginRight: type === "slope" ? 90 : 20,
        marginBottom: 40,
        style: {
            fontFamily: "inherit",
            fontSize: `${fontSize}px`,
            background: "transparent",
            color: t.inkSoft,
            overflow: "visible",
            fontVariantNumeric: "lining-nums tabular-nums",
        },
        x: spec.horizontal ? yScale : xScale,
        y: spec.horizontal ? xScale : yScale,
        ...(spec.series && type !== "slope"
            ? { color: { legend: seriesValues.length > 1, domain: seriesValues, range: spec.colors || t.series.slice(0, Math.max(1, seriesValues.length)) } }
            : {}),
        ...(spec.series && type === "slope" ? { color: { domain: seriesValues, range: spec.colors || t.series } } : {}),
        marks: buildMarks(Plot, spec, data, t),
    });

    // grid and axis colors
    plot.querySelectorAll("[aria-label^='y-grid'] line, [aria-label^='x-grid'] line").forEach((l) => (l.setAttribute("stroke", t.grid)));
    plot.querySelectorAll("[aria-label$='-axis tick'] line").forEach((l) => l.setAttribute("stroke", t.axis));
    plot.querySelectorAll("[aria-label$='-axis tick label'] text, [aria-label$='-axis label'] text").forEach((l) => l.setAttribute("fill", t.inkSoft));
    return plot;
}

function dataTable(spec, data) {
    const cols = spec.columns || Object.keys(data[0] || {});
    const fmt = (v) => (v instanceof Date ? v.toISOString().slice(0, 10) : v == null ? "" : String(v));
    const details = document.createElement("details");
    details.className = "viz-data";
    const summary = document.createElement("summary");
    summary.textContent = `Data table (${data.length} rows)`;
    details.appendChild(summary);
    const wrap = document.createElement("div");
    wrap.className = "gh-table-wrap";
    const table = document.createElement("table");
    table.innerHTML =
        `<thead><tr>${cols.map((c) => `<th${typeof data[0]?.[c] === "number" ? " data-num" : ""}>${c}</th>`).join("")}</tr></thead>` +
        `<tbody>${data.map((d) => `<tr>${cols.map((c) => `<td${typeof d[c] === "number" ? " data-num" : ""}>${fmt(d[c])}</td>`).join("")}</tr>`).join("")}</tbody>`;
    wrap.appendChild(table);
    details.appendChild(wrap);
    return details;
}

function readSpec(el) {
    const script = el.querySelector("script[type='application/json']");
    let spec = {};
    try {
        if (el.dataset.viz) spec = JSON.parse(el.dataset.viz);
        if (script) spec = { ...spec, ...JSON.parse(script.textContent) };
    } catch (e) {
        throw new Error("The chart spec is not valid JSON");
    }
    return spec;
}

function fail(el, err) {
    const p = document.createElement("p");
    p.className = "viz-error";
    p.textContent = `Chart could not render: ${err.message}`;
    el.insertBefore(p, el.firstChild);
}

async function mount(el) {
    let spec, data, Plot;
    try {
        spec = readSpec(el);
        [Plot, data] = await Promise.all([loadPlot(), resolveData(el, spec)]);
    } catch (err) {
        return fail(el, err);
    }
    if (!data.length) return fail(el, new Error("the data set is empty"));
    if (spec.x && !(spec.x in data[0])) return fail(el, new Error(`no column named "${spec.x}"`));
    if (spec.y && !(spec.y in data[0]) && spec.type !== "histogram") return fail(el, new Error(`no column named "${spec.y}"`));

    const host = document.createElement("div");
    host.className = "viz-plot";
    host.setAttribute("role", "img");
    const cap = el.querySelector("figcaption");
    if (cap) host.setAttribute("aria-label", cap.textContent.trim());
    el.insertBefore(host, cap || null);

    let plot = null;
    const draw = () => {
        try {
            const next = render(Plot, host, spec, data);
            if (plot) plot.replaceWith(next); else host.appendChild(next);
            plot = next;
        } catch (err) {
            fail(el, err);
        }
    };
    draw();

    if (spec.tableView !== false) {
        el.insertBefore(dataTable(spec, data), cap || null);
    }

    // redraw on width change and on color scheme change
    let frame = 0;
    const ro = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(draw);
    });
    ro.observe(host);
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", draw);
}

// Inline sparklines: <span class="sparkline" data-values="3,5,4,8,7"></span>
function sparklines() {
    document.querySelectorAll(".sparkline[data-values]").forEach((el) => {
        const vals = el.dataset.values.split(/[,\s]+/).map(Number).filter((n) => !Number.isNaN(n));
        if (vals.length < 2) return;
        const w = 80, h = 20, pad = 2;
        const min = Math.min(...vals), max = Math.max(...vals);
        const sx = (i) => pad + (i / (vals.length - 1)) * (w - pad * 2);
        const sy = (v) => (max === min ? h / 2 : h - pad - ((v - min) / (max - min)) * (h - pad * 2));
        const d = vals.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join("");
        const last = vals[vals.length - 1];
        el.innerHTML = `<svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true"><path d="${d}" fill="none" stroke="currentColor" stroke-width="1.25"/><circle cx="${sx(vals.length - 1)}" cy="${sy(last)}" r="2" fill="currentColor"/></svg>`;
        if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", `Trend: ${vals.join(", ")}`);
        el.setAttribute("role", "img");
    });
}

export default function viz() {
    sparklines();
    const figs = document.querySelectorAll("figure.viz, [data-viz]");
    if (!figs.length) return;
    figs.forEach((el) => {
        el.classList.add("viz");
        mount(el);
    });
}
